import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Building2 } from "lucide-react";

export default function Login() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  React.useEffect(() => {
    if (user) {
      const home =
        user.role === "super_admin" ? "/a/dashboard" :
        user.role === "estate_manager" ? "/m/dashboard" : "/r/dashboard";
      navigate(home, { replace: true });
    }
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) {
      const home =
        res.user.role === "super_admin" ? "/a/dashboard" :
        res.user.role === "estate_manager" ? "/m/dashboard" : "/r/dashboard";
      navigate(location.state?.from?.pathname || home, { replace: true });
      toast.success(`Welcome back, ${res.user.name}`);
    } else {
      toast.error(res.error);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-white">
      {/* Left: form */}
      <div className="flex flex-col justify-between p-6 sm:p-8 md:p-16">
        <div className="flex items-center gap-3">
          <img src="/icons/icon-192.png" alt="HHL logo" className="h-11 w-11 border border-zinc-200" />
          <div>
            <div className="font-display text-2xl font-black tracking-tighter leading-none">HHL</div>
            <div className="label-eyebrow mt-1">Estate Service Management</div>
          </div>
        </div>

        <div className="max-w-md w-full mx-auto">
          <div className="label-eyebrow" data-testid="login-eyebrow">Sign In</div>
          <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter leading-[0.95] mt-3">
            Welcome<br />back.
          </h1>
          <p className="text-sm text-zinc-600 mt-4 mb-8">
            Manage estates, residents, and tickets from a single control room.
          </p>

          <form onSubmit={handleSubmit} className="space-y-5" data-testid="login-form">
            <div>
              <Label className="label-eyebrow" htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email-input"
                className="mt-2 rounded-none h-11 border-zinc-300 focus-visible:ring-0 focus-visible:border-black"
                placeholder="you@example.com"
              />
            </div>
            <div>
              <Label className="label-eyebrow" htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password-input"
                className="mt-2 rounded-none h-11 border-zinc-300 focus-visible:ring-0 focus-visible:border-black"
                placeholder="••••••••"
              />
            </div>
            <Button
              type="submit"
              disabled={loading}
              data-testid="login-submit-button"
              className="w-full h-11 rounded-none bg-black text-white hover:bg-zinc-800 font-semibold uppercase tracking-wider text-xs"
            >
              {loading ? "Signing in…" : "Sign In"}
            </Button>
          </form>
        </div>

        <div className="text-xs text-zinc-500">
          © {new Date().getFullYear()} HHL Estate Services
        </div>
      </div>

      {/* Right: visual */}
      <div className="hidden lg:block relative bg-zinc-900 overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?crop=entropy&cs=srgb&fm=jpg&q=85"
          alt="Modern apartment building"
          className="absolute inset-0 w-full h-full object-cover opacity-90"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-12 text-white">
          <Building2 className="h-10 w-10 mb-4" strokeWidth={1.5} />
          <div className="font-display text-3xl font-black tracking-tight leading-tight">
            One dashboard.<br />Every estate.<br />Every ticket.
          </div>
          <div className="mt-4 text-sm text-white/70 max-w-sm">
            Coordinate super admins, estate managers, and residents in one operational control room.
          </div>
        </div>
      </div>
    </div>
  );
}
